const ADS_ID = (import.meta.env.VITE_GOOGLE_ADS_ID || '').trim();
// Prefer explicit GA env var, but fall back to Firebase's measurement id (same G-... value in many setups)
const GA_ID = (
  import.meta.env.VITE_GA_MEASUREMENT_ID ||
  import.meta.env.VITE_FIREBASE_MEASUREMENT_ID ||
  ''
).trim();

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function isLikelyIOS() {
  if (!isBrowser()) return false;
  const ua = window.navigator.userAgent || '';
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS can report "Macintosh" in the UA while still having touch points
    (ua.includes('Macintosh') && window.navigator.maxTouchPoints > 1)
  );
}

function scheduleIdle(fn) {
  if (!isBrowser()) return;
  const run = () => {
    try {
      fn();
    } catch (_) {
      // Analytics must never crash the app
    }
  };

  // Prefer idle time to reduce main-thread + network contention on page load (notably iOS Safari).
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    window.setTimeout(run, 750);
  }
}

function getOrInitState() {
  window.__gtagInit = window.__gtagInit || { loaded: false, configured: {} };
  return window.__gtagInit;
}

function ensureGtagBootstrap() {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line func-names
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };
}

function hasAnyGtagScript() {
  return Boolean(
    document.querySelector('script[src^="https://www.googletagmanager.com/gtag/js?id="]')
  );
}

function ensureGtagScript(tagId) {
  const state = getOrInitState();
  if (state.loaded || hasAnyGtagScript()) return;
  if (!tagId) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagId)}`;
  script.id = 'google-ads-gtag-js';
  document.head.appendChild(script);
  state.loaded = true;
}

function configOnce(id, params = {}) {
  if (!id) return;
  const state = getOrInitState();
  if (state.configured[id]) return;
  state.configured[id] = 1;

  // Minimal verification hook: should be 1 in production.
  if (id === ADS_ID) {
    window.__googleAdsGtagConfigCount = (window.__googleAdsGtagConfigCount || 0) + 1;
  }

  window.gtag('config', id, params);
}

export function trackPageView(path) {
  if (!isBrowser()) return;

  const page_location = window.location.href;
  const page_path = path;
  const page_title = document.title;
  const page_referrer = document.referrer;

  if (!import.meta.env.PROD) return;

  // Wait for gtag to be available
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_location,
      page_path,
      page_title,
      page_referrer,
    });
  }
}

export function initGoogleAdsTag() {
  if (!isBrowser()) return;

  const initialPagePath = `${window.location.pathname}${window.location.search}`;

  if (!import.meta.env.PROD) return;

  if (!GA_ID) {
    console.error('GA Measurement ID missing in production build. Analytics disabled.');
    return;
  }

  const runInit = () => {
    ensureGtagBootstrap();
    window.gtag('js', new Date());

    // Load the base gtag script using the GA id (required for GA4 collection).
    ensureGtagScript(GA_ID);

    // Configure Ads tag if present (optional).
    if (ADS_ID) {
      configOnce(ADS_ID);
    }

    // Preserve existing GA measurement config previously loaded in app bootstrap.
    // Disable automatic page view so we can manually control it (and syncing with router).
    configOnce(GA_ID, {
      send_page_view: false,
      page_location: window.location.href,
      page_path: initialPagePath,
    });

    // Explicitly send the first page view to capture session_start with correct geo data.
    // On iOS, we delay tag initialization to reduce startup resource contention.
    trackPageView(initialPagePath);
  };

  if (isLikelyIOS()) {
    scheduleIdle(runInit);
    return;
  }

  runInit();
}
